/**
 * Formats activity log action descriptions to be clear and human-readable.
 * Displays new detailed sentences cleanly and converts legacy logs into natural sentences.
 */
export const formatActionMessage = (log) => {
  if (!log) return '—';

  let desc = typeof log === 'string' ? log : log.description || '';
  const actor = typeof log === 'object' && log?.adminName ? log.adminName.trim() : 'Admin';

  // If the log is already a new detailed natural sentence (e.g., contains actor or action verbs)
  if (
    /assigned a new/i.test(desc) ||
    /changed .* password/i.test(desc) ||
    /changed .* role/i.test(desc) ||
    /changed .* name/i.test(desc) ||
    /added a new/i.test(desc) ||
    /created a new/i.test(desc) ||
    /updated .* on .* page/i.test(desc) ||
    /updated .* in .* section/i.test(desc) ||
    /removed .* from .* page/i.test(desc) ||
    /revealed secret/i.test(desc)
  ) {
    return desc;
  }

  // Clean up legacy section-on-page pattern: Created Page: "Template" on page "Page"
  const legacySectionMatch = desc.match(/^created page:\s*"([^"]+)"\s*on page\s*"([^"]+)"/i);
  if (legacySectionMatch) {
    return `${actor} assigned a new "${legacySectionMatch[1]}" template to "${legacySectionMatch[2]}" page`;
  }

  // Clean up legacy section update: Updated Page: "Template" on page "Page"
  const legacySectionUpdateMatch = desc.match(/^updated page:\s*"([^"]+)"\s*on page\s*"([^"]+)"/i);
  if (legacySectionUpdateMatch) {
    return `${actor} updated "${legacySectionUpdateMatch[1]}" section on "${legacySectionUpdateMatch[2]}" page`;
  }

  // Clean up legacy route verb patterns
  if (/^updated (admin|update):\s*"([^"]+)"/i.test(desc)) {
    const targetMatch = desc.match(/^updated (?:admin|update):\s*"([^"]+)"/i);
    return `${actor} updated admin profile of "${targetMatch ? targetMatch[1] : 'admin'}"`;
  }
  if (/^created (?:admin|add):\s*"([^"]+)"/i.test(desc)) {
    const targetMatch = desc.match(/^created (?:admin|add):\s*"([^"]+)"/i);
    return `${actor} added a new admin "${targetMatch ? targetMatch[1] : 'admin'}"`;
  }
  if (/^deleted (?:admin|delete):\s*"([^"]+)"/i.test(desc)) {
    const targetMatch = desc.match(/^deleted (?:admin|delete):\s*"([^"]+)"/i);
    return `${actor} removed admin "${targetMatch ? targetMatch[1] : 'admin'}"`;
  }

  if (/^updated update$/i.test(desc.trim())) {
    return `${actor} updated admin profile`;
  }
  if (/^created add$/i.test(desc.trim())) {
    return `${actor} added a new admin`;
  }
  if (/^deleted delete$/i.test(desc.trim())) {
    return `${actor} removed an admin`;
  }

  // If description was still empty, generate a readable fallback
  if (!desc.trim() && typeof log === 'object') {
    const method = log.method || '';
    const module = formatLogModule(log.module, log.path);
    const actionMap = {
      POST: 'created',
      PUT: 'updated',
      PATCH: 'updated',
      DELETE: 'deleted',
      GET: 'viewed',
    };
    const action = actionMap[method] || method?.toLowerCase() || 'modified';
    return `${actor} ${action} ${module}`;
  }

  return desc;
};

/**
 * Normalizes the module name if it was derived from an action verb.
 */
export const formatLogModule = (module, path = '') => {
  if (!module) return '—';
  const lower = module.toLowerCase();
  if (lower === 'page-sections' || path.includes('/sections') || path.includes('/components')) {
    return 'section';
  }
  if (['update', 'add', 'delete', 'all-admins', 'status'].includes(lower) || path.includes('/admin/')) {
    return 'admin';
  }
  return module;
};


